import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDay, fmtDate } from "@/lib/utils";
import { plainError } from "@/lib/plain-error";

/**
 * The employee's own view of a disciplinary action, and the only place it can
 * be acknowledged.
 *
 * HQ-DISCIPLINE-ROUTE-001. The notification and the dashboard item both pointed
 * at /discipline, which is not a route — the write-up was unreachable from the
 * alert about it, and the acknowledgment the record was waiting on could not be
 * given. (The database could not have recorded it either; see
 * sql/2026-12-29_discipline_acknowledgment.sql.)
 *
 * Access is not enforced here. hq_disciplinary_actions carries
 * hq_discipline_select_own, so a different employee's id in the URL returns no
 * row — the same answer as an id that does not exist, which is the right amount
 * to tell someone about a colleague's write-up.
 */
interface Action {
  id: string;
  employee_id: string;
  issued_on: string | null;
  category: string | null;
  severity: string | null;
  incident_description: string;
  policy_violated: string | null;
  action_taken: string;
  improvement_plan: string | null;
  next_review_date: string | null;
  employee_signed_at: string | null;
  employee_comments: string | null;
}

export function DisciplineAcknowledge() {
  const { id } = useParams<{ id: string }>();
  const { employee } = useAuth();
  const [row, setRow] = useState<Action | null>(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, [id, employee?.id]);

  async function load() {
    if (!id || !employee) return;
    setLoading(true);
    const { data } = await supabase
      .from("hq_disciplinary_actions").select("*").eq("id", id).maybeSingle();
    const r = (data as Action | null) ?? null;
    setRow(r);
    setComments(r?.employee_comments ?? "");
    setLoading(false);
  }

  async function acknowledge() {
    if (!row) return;
    setBusy(true); setError(null);
    // employee_signed_at is set here and overwritten with now() by the database
    // trigger — a signature records when it happened, it is not a value to pick.
    const { error: err } = await supabase
      .from("hq_disciplinary_actions")
      .update({ employee_signed_at: new Date().toISOString(), employee_comments: comments || null })
      .eq("id", row.id);
    setBusy(false);
    if (err) { setError(plainError(err)); return; }
    await load();
  }

  if (loading) return <div className="card py-10 text-center text-sm text-gray2">Loading…</div>;

  if (!row) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="card py-10 text-center">
          <h1 className="text-lg font-semibold">Not found</h1>
          <p className="mt-2 text-sm text-gray2">
            This record does not exist, or it is not yours to read.
          </p>
          <Link to="/" className="btn btn-ghost mt-4 inline-block">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const signed = !!row.employee_signed_at;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Disciplinary action</h1>
        <p className="text-sm text-gray2">Issued {fmtDay(row.issued_on)}</p>
      </div>

      <div className="card space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {row.severity && <span className="pill pill-warn">{row.severity.replace(/_/g, " ")}</span>}
          {row.category && <span className="pill pill-mint">{row.category}</span>}
          {signed
            ? <span className="pill pill-ok">✓ Acknowledged {fmtDate(row.employee_signed_at)}</span>
            : <span className="pill pill-warn">Awaiting your acknowledgment</span>}
        </div>

        {/* Read-only for the person acknowledging it. The database refuses an
            edit to any of these from the employee the record concerns. */}
        <Field label="What happened">{row.incident_description}</Field>
        {row.policy_violated && <Field label="Policy">{row.policy_violated}</Field>}
        <Field label="Action taken">{row.action_taken}</Field>
        {row.improvement_plan && <Field label="Improvement plan">{row.improvement_plan}</Field>}
        {row.next_review_date && <Field label="Next review">{fmtDay(row.next_review_date)}</Field>}
      </div>

      <div className="card space-y-3">
        <h2 className="font-semibold">Your response</h2>
        <p className="text-sm text-gray2">
          Acknowledging records that you have read this. It does not mean you agree with it —
          add anything you want on the record below.
        </p>
        {error && <div className="rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}
        {signed ? (
          <>
            {row.employee_comments
              ? <Field label="Your comments">{row.employee_comments}</Field>
              : <p className="text-sm text-gray2">You acknowledged this without comment.</p>}
            <p className="text-xs text-gray2">
              Acknowledged {fmtDate(row.employee_signed_at)}. This cannot be changed.
            </p>
          </>
        ) : (
          <>
            <textarea
              className="input min-h-[100px]"
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder="Optional — your account of it, or anything you want recorded alongside."
              aria-label="Your comments"
            />
            <div className="flex justify-end">
              <button onClick={() => void acknowledge()} disabled={busy} className="btn">
                {busy ? "Recording…" : "I have read this"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-gray2">{label}</div>
      <div className="mt-1 whitespace-pre-wrap text-sm">{children}</div>
    </div>
  );
}
